import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Download, Pencil, Trash2, Package, Copy, Store as StoreIcon } from 'lucide-react';
import ProductForm from '../components/ProductForm';
import VariantManager from '../components/VariantManager';

const Products = () => {
  const { token, selectedStore } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyingProduct, setCopyingProduct] = useState(null);
  const [copyTargetStores, setCopyTargetStores] = useState([]);
  const [copyStoreCategoryMap, setCopyStoreCategoryMap] = useState({});
  const [allCategoriesByStore, setAllCategoriesByStore] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [productVariants, setProductVariants] = useState({});
  const fetchRequestId = useRef(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchStores();
  }, [debouncedSearch, filterCategory, selectedStore]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Failed to load stores');
    }
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Unknown Store';
  };

  const fetchProducts = async () => {
    // Increment request ID to track latest request
    const currentRequestId = ++fetchRequestId.current;
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (filterCategory) params.append('category_id', filterCategory);
      if (selectedStore) params.append('store_id', selectedStore);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Check if this is still the latest request
      if (currentRequestId !== fetchRequestId.current) {
        return; // Ignore stale response
      }
      
      if (response.ok) {
        const data = await response.json();
        const productsWithQty = [];
        
        for (let i = 0; i < data.length; i++) {
          const prod = data[i];
          const qtyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/products/${prod.id}/total-quantity`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          // Check again if still latest request
          if (currentRequestId !== fetchRequestId.current) {
            return;
          }
          
          if (qtyResponse.ok) {
            const qtyData = await qtyResponse.json();
            productsWithQty.push({ ...prod, total_quantity: qtyData.total_quantity });
          } else {
            productsWithQty.push({ ...prod, total_quantity: 0 });
          }
        }
        
        // Final check before setting state
        if (currentRequestId === fetchRequestId.current) {
          setProducts(productsWithQty);
        }
      }
    } catch (error) {
      if (currentRequestId === fetchRequestId.current) {
        toast.error('Failed to load products');
      }
    } finally {
      if (currentRequestId === fetchRequestId.current) {
        setLoading(false);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedStore) params.append('store_id', selectedStore);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Product deleted');
        fetchProducts();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete product');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const openCopyDialog = async (product) => {
    setCopyingProduct(product);
    setCopyTargetStores([]);
    setCopyStoreCategoryMap({});
    setCopyDialogOpen(true);
  };

  const fetchCategoriesForCopyStore = async (storeId) => {
    if (allCategoriesByStore[storeId]) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories?store_id=${storeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllCategoriesByStore(prev => ({ ...prev, [storeId]: data }));
      }
    } catch (error) {
      console.error('Failed to load categories for store');
    }
  };

  const toggleCopyStoreSelection = async (storeId) => {
    setCopyTargetStores(prev => {
      if (prev.includes(storeId)) {
        // Remove from selected
        const newSelected = prev.filter(id => id !== storeId);
        setCopyStoreCategoryMap(prevMap => {
          const newMap = { ...prevMap };
          delete newMap[storeId];
          return newMap;
        });
        return newSelected;
      } else {
        // Add to selected and fetch categories
        fetchCategoriesForCopyStore(storeId);
        return [...prev, storeId];
      }
    });
  };

  const handleCopyStoreCategoryChange = (storeId, categoryId) => {
    setCopyStoreCategoryMap(prev => ({
      ...prev,
      [storeId]: categoryId
    }));
  };

  const handleCopyToStores = async () => {
    if (!copyingProduct || copyTargetStores.length === 0) {
      toast.error('Please select at least one store');
      return;
    }

    // Validate all stores have categories selected
    for (const storeId of copyTargetStores) {
      if (!copyStoreCategoryMap[storeId]) {
        const store = stores.find(s => s.id === storeId);
        toast.error(`Please select a category for ${store?.name || 'store'}`);
        return;
      }
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${copyingProduct.id}/copy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          store_ids: copyTargetStores,
          category_ids: copyStoreCategoryMap
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setCopyDialogOpen(false);
        setCopyingProduct(null);
        setCopyTargetStores([]);
        setCopyStoreCategoryMap({});
        fetchProducts();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to copy product');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const renderCopyCategoryOptions = (storeId) => {
    const storeCategories = allCategoriesByStore[storeId] || [];
    const mainCategories = storeCategories.filter(c => !c.parent_id);
    
    const options = [];
    for (let i = 0; i < mainCategories.length; i++) {
      const mainCat = mainCategories[i];
      options.push(
        <option key={mainCat.id} value={mainCat.id}>{mainCat.name}</option>
      );
      
      const subcats = storeCategories.filter(c => c.parent_id === mainCat.id);
      for (let j = 0; j < subcats.length; j++) {
        const subcat = subcats[j];
        options.push(
          <option key={subcat.id} value={subcat.id}>  └─ {subcat.name}</option>
        );
      }
    }
    return options;
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedStore) params.append('store_id', selectedStore);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/products?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'products.csv';
        a.click();
        toast.success('Products exported');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setDialogOpen(true);
  };

  const openVariantDialog = (product) => {
    setSelectedProduct(product);
    setVariantDialogOpen(true);
  };

  const toggleExpandProduct = async (productId) => {
    if (expandedProductId === productId) {
      setExpandedProductId(null);
    } else {
      setExpandedProductId(productId);
      // Fetch variants for this product
      if (!productVariants[productId]) {
        await fetchProductVariants(productId);
      }
    }
  };

  const fetchProductVariants = async (productId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}/variants`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setProductVariants(prev => ({ ...prev, [productId]: data }));
      }
    } catch (error) {
      console.error('Failed to load variants');
    }
  };

  const getVariantLabel = (variant) => {
    const labelParts = [];
    if (variant.ohms) labelParts.push(`${variant.ohms}Ω`);
    if (variant.flavour) labelParts.push(variant.flavour);
    if (variant.colour) labelParts.push(variant.colour);
    if (variant.strength) labelParts.push(variant.strength);
    return labelParts.join(' / ');
  };

  const getCategoryName = (categoryId) => {
    let category = null;
    for (let i = 0; i < categories.length; i++) {
      if (categories[i].id === categoryId) {
        category = categories[i];
        break;
      }
    }
    
    if (!category) return 'Unknown';
    
    if (category.parent_id) {
      let parent = null;
      for (let i = 0; i < categories.length; i++) {
        if (categories[i].id === category.parent_id) {
          parent = categories[i];
          break;
        }
      }
      return parent ? `${parent.name} > ${category.name}` : category.name;
    }
    
    return category.name;
  };

  const renderCategoryOptions = () => {
    const mainCategories = [];
    for (let i = 0; i < categories.length; i++) {
      if (!categories[i].parent_id) {
        mainCategories.push(categories[i]);
      }
    }
    
    const options = [];
    for (let i = 0; i < mainCategories.length; i++) {
      const mainCat = mainCategories[i];
      options.push(
        <option key={mainCat.id} value={mainCat.id}>{mainCat.name}</option>
      );
      
      const subcats = [];
      for (let j = 0; j < categories.length; j++) {
        if (categories[j].parent_id === mainCat.id) {
          subcats.push(categories[j]);
        }
      }
      
      for (let k = 0; k < subcats.length; k++) {
        const subcat = subcats[k];
        options.push(
          <option key={subcat.id} value={subcat.id}>  └─ {subcat.name}</option>
        );
      }
    }
    
    return options;
  };

  return (
    <div data-testid="products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="products-title">Products</h1>
          <p className="text-muted-foreground font-body mt-2">Manage your inventory products</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="secondary" data-testid="export-products-btn">
            <Download size={18} strokeWidth={1.5} className="mr-2" />
            Export
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingProduct(null); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="add-product-btn">
                <Plus size={18} strokeWidth={1.5} className="mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-2xl">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </DialogTitle>
              </DialogHeader>
              <ProductForm
                editingProduct={editingProduct}
                categories={categories}
                renderCategoryOptions={renderCategoryOptions}
                onSuccess={() => {
                  setDialogOpen(false);
                  setEditingProduct(null);
                  fetchProducts();
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} strokeWidth={1.5} />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-products-input"
            />
          </div>
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="filter-category-select"
        >
          <option value="">All Categories</option>
          {renderCategoryOptions()}
        </select>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="products-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-border">
              <tr>
                <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</th>
                {!selectedStore && <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Store</th>}
                <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Category</th>
                <th className="text-center p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Total Qty</th>
                <th className="text-center p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Min Stock</th>
                <th className="text-center p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Status</th>
                <th className="text-center p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={selectedStore ? 6 : 7} className="text-center p-8 text-muted-foreground font-mono">Loading...</td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={selectedStore ? 6 : 7} className="text-center p-8 text-muted-foreground font-body">No products found</td>
                </tr>
              ) : (
                products.map((product) => {
                  const isLowStock = product.total_quantity <= product.min_stock_level;
                  const isExpanded = expandedProductId === product.id;
                  const variants = productVariants[product.id] || [];
                  
                  return (
                    <React.Fragment key={product.id}>
                      <tr 
                        className="border-b border-border hover:bg-secondary/50 transition-colors cursor-pointer" 
                        data-testid={`product-row-${product.id}`}
                        onClick={() => toggleExpandProduct(product.id)}
                      >
                        <td className="p-4 font-body font-medium">
                          <div className="flex items-center gap-2">
                            <span className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            {product.name}
                          </div>
                        </td>
                        {!selectedStore && (
                          <td className="p-4">
                            <span className="font-mono text-xs bg-primary/20 text-primary px-2 py-1 rounded flex items-center gap-1 w-fit">
                              <StoreIcon size={12} />
                              {getStoreName(product.store_id)}
                            </span>
                          </td>
                        )}
                        <td className="p-4 font-body text-sm">{getCategoryName(product.category_id)}</td>
                        <td className="p-4 text-center font-mono font-semibold">{product.total_quantity}</td>
                        <td className="p-4 text-center font-mono text-sm text-muted-foreground">{product.min_stock_level}</td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${isLowStock ? 'bg-red-500' : 'bg-green-500'}`} />
                            <span className="font-mono text-xs">{isLowStock ? 'LOW' : 'OK'}</span>
                          </div>
                        </td>
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openCopyDialog(product)}
                              className="p-2 hover:bg-secondary rounded transition-colors"
                              title="Copy to other stores"
                              data-testid={`copy-product-${product.id}`}
                            >
                              <Copy size={16} strokeWidth={1.5} className="text-primary" />
                            </button>
                            <button
                              onClick={() => openVariantDialog(product)}
                              className="p-2 hover:bg-primary/10 text-primary rounded transition-colors"
                              title="Manage Variants"
                              data-testid={`manage-variants-${product.id}`}
                            >
                              <Package size={16} strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => openEditDialog(product)}
                              className="p-2 hover:bg-secondary rounded transition-colors"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Pencil size={16} strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-2 hover:bg-destructive/10 text-destructive rounded transition-colors"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 size={16} strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-secondary/30">
                          <td colSpan={selectedStore ? 6 : 7} className="p-4">
                            <div className="pl-8">
                              <h4 className="font-heading font-semibold text-sm mb-3 text-primary">Variant Quantities:</h4>
                              {variants.length === 0 ? (
                                <p className="text-muted-foreground text-sm">No variants yet. Click the package icon to add variants.</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {variants.map((variant, idx) => {
                                    const label = getVariantLabel(variant);
                                    return (
                                      <div 
                                        key={idx} 
                                        className="flex items-center justify-between p-3 bg-card rounded border border-border"
                                      >
                                        <span className="font-body text-sm">{label}</span>
                                        <span className="font-mono font-semibold text-sm">{variant.quantity} units</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={variantDialogOpen} onOpenChange={setVariantDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl">
              Manage Variants - {selectedProduct ? selectedProduct.name : ''}
            </DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <VariantManager
              product={selectedProduct}
              onUpdate={fetchProducts}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Copy to Stores Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={(open) => { setCopyDialogOpen(open); if (!open) { setCopyingProduct(null); setCopyTargetStores([]); setCopyStoreCategoryMap({}); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="copy-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl">
              Copy Product to Other Stores
            </DialogTitle>
          </DialogHeader>
          {copyingProduct && (
            <div className="space-y-4">
              <div className="bg-secondary p-3 rounded-md border border-border">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Copying Product</p>
                <p className="font-body font-medium">{copyingProduct.name}</p>
                {copyingProduct.store_id && (
                  <p className="text-xs text-muted-foreground mt-1">From: {getStoreName(copyingProduct.store_id)}</p>
                )}
              </div>

              <div className="border border-border rounded-md p-3">
                <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Select Target Stores & Categories</Label>
                <div className="max-h-64 overflow-y-auto space-y-3">
                  {stores.filter(s => s.id !== copyingProduct.store_id).map(store => (
                    <div key={store.id} className="p-2 border border-border rounded bg-card">
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={copyTargetStores.includes(store.id)}
                          onChange={() => toggleCopyStoreSelection(store.id)}
                          className="w-4 h-4"
                          data-testid={`copy-product-store-checkbox-${store.id}`}
                        />
                        <StoreIcon size={14} className="text-muted-foreground" />
                        <span className="text-sm font-medium">{store.name}</span>
                      </label>
                      {copyTargetStores.includes(store.id) && (
                        <div className="ml-6">
                          <select
                            value={copyStoreCategoryMap[store.id] || ''}
                            onChange={(e) => handleCopyStoreCategoryChange(store.id, e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                            data-testid={`copy-product-category-${store.id}`}
                          >
                            <option value="">Select category for {store.name}</option>
                            {renderCopyCategoryOptions(store.id)}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {copyTargetStores.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {copyTargetStores.length} store(s) selected
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={handleCopyToStores} 
                  className="flex-1 bg-primary hover:bg-primary/90" 
                  data-testid="copy-product-submit-btn"
                  disabled={copyTargetStores.length === 0}
                >
                  Copy to {copyTargetStores.length} Store(s)
                </Button>
                <Button type="button" variant="secondary" onClick={() => { setCopyDialogOpen(false); setCopyingProduct(null); setCopyTargetStores([]); setCopyStoreCategoryMap({}); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;